import Button from '@/common/components/atom/button';
import Text from '@/common/components/atom/text/text';
import LocationIcon from '@/common/components/icons/location';
import PhoneIcon from '@/common/components/icons/phone';
import useCustomize from '@/common/hooks/useCustomize';
import { splunkInstance } from '@/common/services/splunk';
import { CENTERS } from '@/common/types/centers';
import classNames from '@/common/utils/classNames';
import { openGoogleMap } from '@/common/utils/openGoogleMap';
import isEmpty from 'lodash/isEmpty';

interface NoBookingContactProps {
  /** Profile centers as Aside passes them (id, name, address, city, center_type, display_number_array, map). */
  centers?: any[];
  displayName: string;
  className?: string;
}

/**
 * The visitor who lands on a no-booking profile mostly came for THIS doctor (owner 2026-10-12), so
 * before any substitute-doctor suggestion, surface the doctor's own clinic phone/address inline.
 * The full "phone-and-address" aside section stays untouched — this is the same data at the
 * decision point instead of several screens down.
 */
const NoBookingContact = ({ centers, displayName, className }: NoBookingContactProps) => {
  const customize = useCustomize(state => state.customize);
  const contactCenters = (centers ?? []).filter((center: any) => String(center.id) !== CENTERS.CONSULT);

  if (customize.partnerKey || contactCenters.length === 0) {
    return null;
  }

  const sendContactEvent = (kind: 'phone' | 'map', centerId: any) => {
    splunkInstance('doctor-profile').sendEvent({
      group: 'doctor profile',
      type: 'no-booking contact',
      event: { kind, center_id: centerId },
    });
  };

  return (
    <div className={classNames('flex flex-col space-y-2', className)}>
      <Text fontSize="sm" fontWeight="medium" className="text-slate-600">
        ارتباط مستقیم با {displayName}
      </Text>
      {contactCenters.map((center: any) => {
        const phoneNumbers = center?.display_number_array;
        const hasLocation = center?.map?.lat != null && center?.map?.lon != null;
        return (
          <div key={center.id} className="flex flex-col p-3 space-y-2 rounded-lg bg-slate-50">
            <Text fontSize="sm" fontWeight="semiBold">
              {center.center_type === 1 ? `مطب ${displayName}` : center.name}
            </Text>
            {(center.address || center.city) && (
              <Text fontSize="sm" className="leading-6 text-slate-600">
                {[center.city, center.address].filter(Boolean).join(' - ')}
              </Text>
            )}
            <div className="flex flex-col space-y-2">
              {typeof phoneNumbers === 'string' && (
                <Button
                  variant="secondary"
                  icon={<PhoneIcon width={20} height={20} />}
                  onClick={() => {
                    sendContactEvent('phone', center.id);
                    location.href = `tel:${phoneNumbers}`;
                  }}
                >
                  {phoneNumbers}
                </Button>
              )}
              {Array.isArray(phoneNumbers) &&
                phoneNumbers.map((phoneNumber: string) => (
                  <Button
                    key={phoneNumber}
                    variant="secondary"
                    icon={<PhoneIcon width={20} height={20} />}
                    onClick={() => {
                      sendContactEvent('phone', center.id);
                      location.href = `tel:${phoneNumber}`;
                    }}
                  >
                    {phoneNumber}
                  </Button>
                ))}
              {hasLocation && (
                <Button
                  variant="secondary"
                  icon={<LocationIcon width={20} height={20} />}
                  onClick={() => {
                    sendContactEvent('map', center.id);
                    openGoogleMap({ lat: center.map.lat, lon: center.map.lon });
                  }}
                >
                  مشاهده در نقشه و مسیریابی
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NoBookingContact;
